import { useEffect, useMemo, useRef, type FC } from 'react';
import { cn } from '@/client/lib/utils';
import type { SkillInfo } from '@/client/lib/useSkills';

const WORD_RE = /[a-zA-Z0-9_-]/;

/**
 * Active state of the `/` slash menu — derived from the composer text and
 * caret position. ``start`` points at the `/` character; ``query`` is the
 * partial skill name typed after it.
 */
export interface SlashMenuState {
  start: number;
  query: string;
}

/**
 * Compute the menu state from the composer text and caret position.
 *
 * Returns a state object when the caret sits inside a `/skill-prefix` token
 * at a word boundary, ``null`` otherwise. The match is anchored by:
 *   - a `/` somewhere at or before the caret,
 *   - only word chars (`[a-zA-Z0-9_-]`) between the `/` and the caret,
 *   - whitespace or start-of-text immediately before the `/`.
 *
 * Anchoring on a word boundary keeps things like a URL path (`https://...`)
 * from accidentally opening the menu mid-word.
 */
export function getSlashMenuState(text: string, caret: number): SlashMenuState | null {
  for (let i = caret - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === '/') {
      if (i > 0 && !/\s/.test(text[i - 1])) return null;
      return { start: i, query: text.slice(i + 1, caret) };
    }
    if (!WORD_RE.test(ch)) return null;
  }
  return null;
}

interface SlashCommandMenuProps {
  skills: SkillInfo[];
  state: SlashMenuState;
  highlightIndex: number;
  onSelect: (skill: SkillInfo) => void;
  onHover: (index: number) => void;
}

/**
 * Floating menu rendered above the composer when the user is typing a
 * `/skill-name` token. Selection is keyboard-driven by the Composer
 * (Enter/Tab/Arrow keys) — this component is purely presentational so the
 * Composer can keep keyboard focus on the textarea.
 */
export const SlashCommandMenu: FC<SlashCommandMenuProps> = ({
  skills,
  state,
  highlightIndex,
  onSelect,
  onHover,
}) => {
  const filtered = useMemo(
    () => filterSkills(skills, state.query),
    [skills, state.query],
  );

  const listRef = useRef<HTMLUListElement>(null);
  useEffect(() => {
    const item = listRef.current?.querySelector<HTMLLIElement>(
      `[data-index="${highlightIndex}"]`,
    );
    item?.scrollIntoView({ block: 'nearest' });
  }, [highlightIndex]);

  if (filtered.length === 0) return null;

  return (
    <div
      className="aui-slash-menu absolute bottom-full left-0 right-0 z-20 mb-2 max-h-72 w-full overflow-hidden rounded-xl border border-input bg-popover shadow-lg"
      role="listbox"
      aria-label="Slash command menu"
    >
      <div className="border-b px-3 py-1.5 text-xs font-medium text-muted-foreground">
        Skills
      </div>
      <ul ref={listRef} className="max-h-60 overflow-y-auto py-1">
        {filtered.map((skill, idx) => (
          <li
            key={skill.name}
            data-index={idx}
            role="option"
            aria-selected={idx === highlightIndex}
            className={cn(
              'flex cursor-pointer flex-col gap-0.5 px-3 py-1.5 text-sm',
              idx === highlightIndex
                ? 'bg-accent text-accent-foreground'
                : 'hover:bg-accent/50',
            )}
            onMouseEnter={() => onHover(idx)}
            onMouseDown={(e) => {
              e.preventDefault();
              onSelect(skill);
            }}
          >
            <span className="font-mono text-xs font-semibold">/{skill.name}</span>
            <span className="line-clamp-1 text-xs text-muted-foreground">
              {skill.description}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * Substring-match plus subsequence-match fallback so typing `/opp` still
 * matches `opportunities` even if the user types out-of-order chars.
 */
export function filterSkills(skills: SkillInfo[], query: string): SkillInfo[] {
  if (!query) return skills;
  const q = query.toLowerCase();
  return skills.filter(
    (s) => s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q),
  );
}

const SLASH_TOKEN_RE = /(^|\s)\/([a-zA-Z][\w-]*)/g;

/**
 * Returns the ordered, deduplicated list of skill names referenced by
 * ``/skill-name`` tokens at word boundaries — used both for the send-time
 * body embedding and the chip-rendering pass in user messages.
 */
export function findReferencedSkills(text: string, skills: SkillInfo[]): SkillInfo[] {
  if (skills.length === 0 || !text.includes('/')) return [];
  const byName = new Map(skills.map((s) => [s.name, s]));
  const seen = new Set<string>();
  const out: SkillInfo[] = [];
  for (const m of text.matchAll(SLASH_TOKEN_RE)) {
    const name = m[2];
    const skill = byName.get(name);
    if (skill && !seen.has(name)) {
      seen.add(name);
      out.push(skill);
    }
  }
  return out;
}

/**
 * Returns the start index + name of a known ``/skill-name`` token whose
 * right edge sits exactly at ``caret``, or null. Used by the Composer's
 * Backspace handler to atomically delete the whole token in one keystroke.
 */
export function tokenEndingAt(
  text: string,
  caret: number,
  known: Set<string>,
): { start: number; name: string } | null {
  // Match the token at the end of `before`. The leading group anchors
  // word boundary the same way SLASH_TOKEN_RE does.
  const match = text.slice(0, caret).match(/(^|\s)\/([a-zA-Z][\w-]*)$/);
  if (!match) return null;
  const name = match[2];
  if (!known.has(name)) return null;
  return { start: caret - 1 - name.length, name };
}
